import React, { useState } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Modal,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useAuth } from "../../../context/AuthContext";
import { useDashboardTheme } from "../../../context/DashboardThemeContext";
import { useAlert } from "../../../context/AlertContext";

const getUserName = (user) => user?.nombre || user?.name || "";
const getUserEmail = (user) => user?.email || user?.correo || "";
const getUserPhone = (user) => user?.telefono || user?.phone || "";

const GenerateTemporaryKey = ({
  open,
  onClose,
  user,
  onDone,
  empresaNombre = "la empresa",
  loginUrl = "",
  regenerateEndpoint = "/api/auth/users/regenerate-temporary-key/",
  sendEmailEndpoint = "/api/auth/users/send-temporary-key-email/",
}) => {
  const { accessToken, logout } = useAuth();
  const { dashboardTheme } = useDashboardTheme();
  const alert = useAlert();

  const [loading, setLoading] = useState(false);
  const [sendingMail, setSendingMail] = useState(false);
  const [clave, setClave] = useState("");

  const notify = (message, severity = "success") => {
    if (typeof alert?.showAlert === "function") {
      alert.showAlert(message, severity);
    }

    if (typeof onDone === "function") {
      onDone(message, severity);
    }
  };

  const getToken = () => accessToken || localStorage.getItem("accessToken") || "";

  const parseJsonSafe = async (res) => {
    try {
      return await res.json();
    } catch {
      return { ok: false, error: "Respuesta inválida del servidor" };
    }
  };

  const authFetch = async (url, options = {}) => {
    const token = getToken();

    const headers = {
      Accept: "application/json",
      ...(options.headers || {}),
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };

  const primaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    bgcolor: dashboardTheme.button_color,
    color: dashboardTheme.button_text_color,
    boxShadow: "none",
    "&:hover": {
      bgcolor: dashboardTheme.button_hover_color || dashboardTheme.button_color,
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    color: dashboardTheme.text_color,
    borderColor: dashboardTheme.border_color,
    "&:hover": {
      borderColor: dashboardTheme.primary_color,
      bgcolor: `${dashboardTheme.primary_color}12`,
    },
  };

  const generarClave = async () => {
    if (!user) return;

    try {
      setLoading(true);

      const res = await authFetch(regenerateEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id: user.id }),
      });

      const data = await parseJsonSafe(res);

      if (res.status === 401) {
        notify("Sesión vencida. Inicia sesión nuevamente.", "error");
        logout();
        return;
      }

      if (res.ok && data.ok) {
        setClave(data.clave_temporal);
        notify("Clave temporal generada correctamente", "success");
      } else {
        notify(data?.error || "Error al generar la clave", "error");
      }
    } catch {
      notify("Error de conexión", "error");
    } finally {
      setLoading(false);
    }
  };

  const copiarClave = async () => {
    await navigator.clipboard.writeText(clave);
    if (typeof alert?.showAlert === "function") {
      alert.showAlert("Clave copiada al portapapeles", "info");
    }
  };

  const enviarWhatsApp = () => {
    if (!loginUrl) {
      notify("Configura primero la URL de login.", "error");
      return;
    }

    const numero = String(getUserPhone(user) || "").replace(/\D/g, "");
    const numeroFinal = numero.startsWith("593") ? numero : `593${numero}`;

    const mensaje = encodeURIComponent(
      `Hola ${getUserName(user)},\n\nTe han generado una nueva clave temporal de acceso en ${empresaNombre}.\n\nCorreo: ${getUserEmail(user)}\nClave temporal: ${clave}\n\nPuedes ingresar aquí:\n${loginUrl}`
    );

    window.open(`https://wa.me/${numeroFinal}?text=${mensaje}`, "_blank");
  };

  const enviarCorreo = async () => {
    if (!user || sendingMail) return;

    if (!loginUrl) {
      notify("Configura primero la URL de login.", "error");
      return;
    }

    try {
      setSendingMail(true);

      const res = await authFetch(sendEmailEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: user.id,
          login_url: loginUrl,
        }),
      });

      const data = await parseJsonSafe(res);

      if (res.status === 401) {
        notify("Sesión vencida. Inicia sesión nuevamente.", "error");
        logout();
        return;
      }

      if (res.ok && data.ok) {
        notify(data?.msg || data?.message || "Clave enviada por correo", "success");
      } else {
        notify(data?.error || "Error al enviar correo", "error");
      }
    } catch {
      notify("Error de conexión", "error");
    } finally {
      setSendingMail(false);
    }
  };

  const cerrar = () => {
    if (loading || sendingMail) return;
    setClave("");
    onClose();
  };

  return (
    <Modal open={open} onClose={cerrar}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(460px, calc(100vw - 32px))",
          bgcolor: dashboardTheme.card_background,
          borderRadius: "16px",
          p: { xs: 2, sm: 3 },
          border: `1px solid ${dashboardTheme.border_color}`,
          boxShadow:
            dashboardTheme.theme_mode === "dark"
              ? "0 24px 60px rgba(0,0,0,0.45)"
              : "0 24px 60px rgba(17,24,39,0.18)",
          outline: "none",
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: 18,
            mb: 1,
            color: dashboardTheme.text_color,
          }}
        >
          Generar clave temporal
        </Typography>

        <Typography sx={{ color: dashboardTheme.muted_text_color, mb: 2 }}>
          Usuario: <strong>{getUserName(user) || "—"}</strong>
        </Typography>

        {!clave && (
          <Button
            fullWidth
            disabled={loading}
            onClick={generarClave}
            sx={primaryButtonSx}
          >
            {loading ? "Generando..." : "Generar clave temporal"}
          </Button>
        )}

        {clave && (
          <>
            <Divider sx={{ my: 2, borderColor: dashboardTheme.border_color }} />

            <Typography sx={{ fontSize: 14, mb: 1, color: dashboardTheme.text_color }}>
              Nueva clave temporal
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                bgcolor:
                  dashboardTheme.theme_mode === "dark"
                    ? "rgba(255,255,255,0.04)"
                    : "#F5F7FA",
                p: 1.5,
                borderRadius: "10px",
                fontWeight: 700,
                letterSpacing: 1,
                color: dashboardTheme.text_color,
                border: `1px solid ${dashboardTheme.border_color}`,
              }}
            >
              {clave}

              <Tooltip title="Copiar">
                <IconButton onClick={copiarClave} sx={{ color: dashboardTheme.text_color }}>
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 2 }}>
              <Button
                fullWidth
                startIcon={<WhatsAppIcon />}
                onClick={enviarWhatsApp}
                sx={{
                  borderRadius: "10px",
                  bgcolor:
                    dashboardTheme.theme_mode === "dark"
                      ? "rgba(34,197,94,0.14)"
                      : "#E8F5E9",
                  color: "#16a34a",
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": {
                    bgcolor:
                      dashboardTheme.theme_mode === "dark"
                        ? "rgba(34,197,94,0.2)"
                        : "#DFF0E3",
                  },
                }}
              >
                WhatsApp
              </Button>

              <Button
                fullWidth
                startIcon={<EmailIcon />}
                onClick={enviarCorreo}
                disabled={sendingMail}
                sx={{
                  borderRadius: "10px",
                  bgcolor:
                    dashboardTheme.theme_mode === "dark"
                      ? `${dashboardTheme.primary_color}22`
                      : `${dashboardTheme.primary_color}14`,
                  color: dashboardTheme.primary_color,
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": {
                    bgcolor:
                      dashboardTheme.theme_mode === "dark"
                        ? `${dashboardTheme.primary_color}33`
                        : `${dashboardTheme.primary_color}22`,
                  },
                }}
              >
                {sendingMail ? "Enviando..." : "Correo"}
              </Button>
            </Stack>
          </>
        )}

        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
          <Button
            onClick={cerrar}
            variant="outlined"
            sx={secondaryButtonSx}
            disabled={loading || sendingMail}
          >
            Cerrar
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};

export default GenerateTemporaryKey;